/* ═══════════════════════════════════════════════════════════════
   The Designer — issuance logic (pure).

   The manual-level issuance model, as computation only — no React, no
   network. The Issuance Desk, the revisions plugin, the Manual rail's
   chips and the addendum summary all read THESE functions, so "what is
   the baseline" and "what changed" have exactly one definition.

   Lifecycle (DESIGNER_PLAN §3.3):
     · design phase — no manual issuance yet: quiet editing, no marks
     · issued phase — the latest manual issuance is the baseline; each
       section's marks are measured against the per-section snapshot its
       manifest entry references; each new issuance re-baselines
   ═══════════════════════════════════════════════════════════════ */

import type {
  ContentBlock,
  Issuance,
  ManualIssuanceManifestEntry,
  ProjectManualIssuance,
  ProjectSpec,
  StandardConfig,
} from '../../shared/types';
import { realIssuances } from '../../shared/types';
import { buildInlineDiff } from '../services/specService';
import { computeNumbering } from '../../shared/body-layout';

// ─── Baseline resolution ───

/** The manual issuance the whole Manual is currently measured against. */
export function latestManualIssuance(
  list: ProjectManualIssuance[] | null | undefined
): ProjectManualIssuance | null {
  if (!list?.length) return null;
  const sorted = [...list].sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
  return sorted[sorted.length - 1];
}

/**
 * The per-section Issuance a spec's revision marks are measured against.
 *
 * Manual model first: the latest manual issuance's manifest entry for this
 * spec points at the exact snapshot it froze. A section with no entry (added
 * to the manual after that issuance) has no baseline — it renders unmarked
 * and reports 'not-issued'.
 *
 * Legacy fallback: projects issued before manual-level records existed carry
 * per-spec issuances only (e.g. the real IFC on 26055-00). With NO manual
 * issuance on the project, the spec's own latest snapshot issuance stays the
 * baseline, so existing marks neither vanish nor re-baseline silently.
 */
export function resolveBaselineIssuance(
  spec: ProjectSpec,
  manual: ProjectManualIssuance | null
): Issuance | null {
  if (manual) {
    const entry = manual.manifest.find((m) => m.specId === spec.id);
    if (!entry) return null;
    const iss = (spec.issuances || []).find((i) => i.id === entry.specIssuanceId);
    return iss?.snapshot ? iss : null;
  }
  const withSnap = realIssuances(spec.issuances).filter((i) => i.snapshot);
  return withSnap.length ? withSnap[withSnap.length - 1] : null;
}

/** The block list diffs compare — excluded blocks never print, so they never
 *  count. Same flattening the export pipeline uses. */
export function flattenForDiff(src: {
  coreContent?: ContentBlock[];
  systems?: Array<{ content?: ContentBlock[] }>;
}): ContentBlock[] {
  return [
    ...(src.coreContent || []),
    ...((src.systems || []).flatMap((sys) => sys.content || []) as ContentBlock[]),
  ].filter((b) => !b.excluded);
}

/** What "unchanged" means everywhere: same blocks, same text, same level. */
function signature(blocks: ContentBlock[]): string {
  return JSON.stringify(blocks.map((b) => [b.id, b.text, b.level]));
}

/** Has this spec's printable content changed since the given baseline? */
export function changedSince(spec: ProjectSpec, baseline: Issuance | null): boolean {
  if (!baseline?.snapshot) return false;
  return signature(flattenForDiff(baseline.snapshot)) !== signature(flattenForDiff(spec));
}

// ─── Manual rail status ───

export type SectionStatus = 'not-issued' | 'clean' | 'edited';

/** Issuance-aware status for a rail chip, measured against the MANUAL-level
 *  baseline (legacy per-spec fallback included — see resolveBaselineIssuance). */
export function sectionStatus(
  spec: ProjectSpec,
  manual: ProjectManualIssuance | null
): { status: SectionStatus; issuanceName?: string } {
  const baseline = resolveBaselineIssuance(spec, manual);
  if (!baseline) return { status: 'not-issued' };
  return {
    status: changedSince(spec, baseline) ? 'edited' : 'clean',
    issuanceName: manual?.name || baseline.name,
  };
}

// ─── Manifest ───

/**
 * The manifest for a manual issuance being created NOW. `changed` records
 * whether the section differed from the PREVIOUS manual baseline: on the
 * first issuance nothing has "changed" — the manual is simply issued; on a
 * re-issue, a section changed if its content differs from its previous
 * baseline snapshot, or if it had no baseline (added since).
 */
export function buildManifest(
  entries: Array<{
    spec: ProjectSpec;
    specIssuanceId: string;
    prevBaseline: Issuance | null;
  }>,
  isFirstIssuance: boolean
): ManualIssuanceManifestEntry[] {
  return entries.map(({ spec, specIssuanceId, prevBaseline }) => ({
    specId: spec.id,
    specIssuanceId,
    changed: isFirstIssuance ? false : !prevBaseline || changedSince(spec, prevBaseline),
  }));
}

// ─── Design-phase safety snapshots (quiet, rolling, recoverable) ───
// Pre-baseline there is no issuance to diff against, so the Designer keeps a
// rolling history instead: auto snapshots stored as Issuance rows flagged
// kind:'auto' inside the spec's existing issuances array (SPEC_STUDIO_PLAN
// §3.4 — no new storage). Capped, hidden from the issuance timeline, never a
// revision-mark baseline, and pruned when the manual actually issues.

/** At most this many auto snapshots ride on one spec row. */
export const AUTO_SNAPSHOT_CAP = 20;

/** Minimum quiet gap between auto snapshots (~Word's AutoRecover cadence). */
export const AUTO_SNAPSHOT_MS = 10 * 60_000;

/** This spec's auto snapshots, oldest first. */
export function autoSnapshots(spec: ProjectSpec): Issuance[] {
  return (spec.issuances || [])
    .filter((i) => i.kind === 'auto' && i.snapshot)
    .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
}

function newAutoId(): string {
  return 'auto-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

/**
 * Take a rolling safety snapshot of the LAST-SAVED state if one is due —
 * called by the autosave path with the spec as it exists BEFORE the pending
 * edits overwrite it, so the state a burst of edits replaced stays
 * recoverable. Returns the new issuances array, or null when not due.
 *
 * Due = the saved content differs from the newest auto snapshot AND the
 * newest one is at least AUTO_SNAPSHOT_MS old (or none exists yet).
 */
export function maybeAutoSnapshot(
  lastSaved: ProjectSpec,
  userName: string | undefined,
  now: number = Date.now()
): Issuance[] | null {
  const autos = autoSnapshots(lastSaved);
  const newest = autos[autos.length - 1];
  const sig = signature(flattenForDiff(lastSaved));
  if (newest?.snapshot) {
    if (signature(flattenForDiff(newest.snapshot)) === sig) return null;
    const age = now - new Date(newest.createdAt || 0).getTime();
    if (age < AUTO_SNAPSHOT_MS) return null;
  }

  const nowIso = new Date(now).toISOString();
  const snap: Issuance = {
    id: newAutoId(),
    name: 'Auto snapshot',
    date: nowIso,
    createdAt: nowIso,
    issuedBy: userName,
    kind: 'auto',
    snapshot: JSON.parse(
      JSON.stringify({
        coreContent: lastSaved.coreContent || [],
        systems: lastSaved.systems || [],
        tokenOverrides: lastSaved.tokenOverrides,
        formaId: lastSaved.formaId,
        csiNumber: lastSaved.csiNumber,
        title: lastSaved.title,
      })
    ),
  };

  // Prune beyond the cap — oldest autos go first; real issuances untouched.
  const next = [...(lastSaved.issuances || []), snap];
  const autosNow = next.filter((i) => i.kind === 'auto');
  if (autosNow.length > AUTO_SNAPSHOT_CAP) {
    const drop = new Set(
      autosNow
        .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''))
        .slice(0, autosNow.length - AUTO_SNAPSHOT_CAP)
        .map((i) => i.id)
    );
    return next.filter((i) => !drop.has(i.id));
  }
  return next;
}

/** Issuing the manual re-baselines the section — the pre-issue safety
 *  snapshots have served their purpose and are dropped. */
export function pruneAutoSnapshots(issuances: Issuance[] | undefined): Issuance[] {
  return (issuances || []).filter((i) => i.kind !== 'auto');
}

// ─── Addendum change summary ───

export interface AddendumChange {
  /** The printed paragraph ref ("1.03", "A.", "PART 2 -") — computed by the
   *  one body-layout brain, so the summary names paragraphs the way the
   *  paper prints them. Empty when the scheme prints nothing there. */
  ref: string;
  kind: 'added' | 'removed' | 'modified';
  /** Opening words of the paragraph, so a change is recognisable at a glance. */
  excerpt: string;
}

export interface SectionChangeSummary {
  specId: string;
  csiNumber: string;
  title: string;
  /** The section had no earlier snapshot — the whole section is new. */
  newSection: boolean;
  changes: AddendumChange[];
}

const EXCERPT_LEN = 90;

function excerptOf(b: { text?: string; blockType?: string } | undefined): string {
  if (!b) return '';
  if (b.blockType === 'table') return '[table]';
  if (b.blockType === 'image') return '[figure]';
  const t = (b.text || '').trim();
  return t.length > EXCERPT_LEN ? t.slice(0, EXCERPT_LEN - 1).trimEnd() + '…' : t;
}

/**
 * Paragraph-level changed refs for one section, computed from the diff
 * (buildInlineDiff — the same engine the marks render from). `prevBlocks`
 * null means the section is new since the previous issuance.
 */
export function computeSectionSummary(
  meta: { specId: string; csiNumber?: string; title?: string },
  prevBlocks: ContentBlock[] | null,
  nextBlocks: ContentBlock[],
  forma: StandardConfig | null
): SectionChangeSummary {
  const base: SectionChangeSummary = {
    specId: meta.specId,
    csiNumber: meta.csiNumber || '',
    title: meta.title || '',
    newSection: prevBlocks === null,
    changes: [],
  };
  if (prevBlocks === null) return base;

  const prev = prevBlocks.filter((b) => !b.excluded);
  const next = nextBlocks.filter((b) => !b.excluded);
  const diff = buildInlineDiff(prev, next);

  // Refs come from the numbering the reader will actually see: removed
  // paragraphs are named by their OLD number, everything else by its new one.
  const prevRefs = new Map(computeNumbering(prev, forma).map((r) => [r.id, r.prefix]));
  const nextRefs = new Map(computeNumbering(next, forma).map((r) => [r.id, r.prefix]));
  const prevById = new Map(prev.map((b) => [b.id, b]));
  const nextById = new Map(next.map((b) => [b.id, b]));

  for (const d of diff) {
    if (d.kind === 'unchanged') continue;
    if (d.kind === 'removed') {
      base.changes.push({
        ref: prevRefs.get(d.id) || '',
        kind: 'removed',
        excerpt: excerptOf(prevById.get(d.id)),
      });
    } else {
      base.changes.push({
        ref: nextRefs.get(d.id) || '',
        kind: d.kind,
        excerpt: excerptOf(nextById.get(d.id)),
      });
    }
  }
  return base;
}

/**
 * The whole addendum summary for one manual issuance: per included section,
 * its paragraph-level changes vs. the PREVIOUS manual issuance (or vs. the
 * section's own previous snapshot under the legacy fallback).
 */
export function computeAddendumSummary(
  issuance: ProjectManualIssuance,
  previous: ProjectManualIssuance | null,
  specs: ProjectSpec[],
  formaForSpec: (spec: ProjectSpec) => StandardConfig | null
): SectionChangeSummary[] {
  const out: SectionChangeSummary[] = [];
  for (const entry of issuance.manifest) {
    const spec = specs.find((s) => s.id === entry.specId);
    if (!spec) continue;
    const thisIss = (spec.issuances || []).find((i) => i.id === entry.specIssuanceId);
    if (!thisIss?.snapshot) continue;
    const prevIss = previous ? resolveBaselineIssuance(spec, previous) : null;
    out.push(
      computeSectionSummary(
        {
          specId: spec.id,
          csiNumber: thisIss.snapshot.csiNumber ?? spec.csiNumber,
          title: thisIss.snapshot.title ?? spec.title,
        },
        prevIss?.snapshot ? flattenForDiff(prevIss.snapshot) : null,
        flattenForDiff(thisIss.snapshot),
        formaForSpec(spec)
      )
    );
  }
  return out;
}
